import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';
import { DataLayerEventContext } from '../../../contexts/dataLayerEventContext';
import { ArrowIcon, HandIcon } from './icons';

const IMAGES = {
  heroPoster: '/images/23ua-lm-4-test/hero-poster.jpg',
  heroVideo: '/images/23ua-lm-4-test/hero.mp4',
  background: '/images/23ua-lm-4-test/bg.svg'
};

const COLORS = [
  {
    id: 'black',
    name: 'Чорний',
    gradient: 'linear-gradient(270deg, #000 0%, #191919 100%)',
    text: 'Це колір, який символізує загадковість та силу. Він дає відчуття стриманості та концентрації на важливих речах. Цей колір використовує багато відомих брендів, щоб підкреслити свою авторитетність та надійність.'
  },
  {
    id: 'violet',
    name: 'Фіолетовий',
    gradient: 'linear-gradient(270deg, #3F1177 0%, #751FDD 100%)',
    text: 'Цей колір — символ елегантності та творчості. Він допомагає заспокоїтися та зосередитися на внутрішньому світі. Фіолетовий сприяє більш глибокому та спокійному сну.'
  },
  {
    id: 'yellow',
    name: 'Жовтий',
    gradient: 'linear-gradient(270deg, #FFD428 0%, #FEDB4E 100%)',
    text: 'Колір, який сяє радістю та оптимізмом. Він символізує невимушеність, сприяє швидкому прийняттю рішень, а також може активувати діяльність головного мозку та поліпшувати пам’ять.'
  },
  {
    id: 'blue',
    name: 'Синій',
    gradient: 'linear-gradient(270deg, #00159F 0%, #001CD2 100%)',
    text: 'Колір спокою та розуму. Він знижує рівень тривожності та сприяє зануренню в глибину власних думок. Недарма небо та море нас заспокоюють.'
  },
  {
    id: 'mint',
    name: 'М’ятний',
    gradient: 'linear-gradient(270deg, #3C9486 0%, #36B6A2 100%)',
    text: 'Символ весни та свіжості. Цей колір не напружує око, надає відчуття умиротворення та затишку. Саме тому м’ятний часто зустрічається в лікарнях та дитячих садочках.'
  },
  {
    id: 'gray',
    name: 'Сірий',
    gradient: 'linear-gradient(270deg, #505353 0%, #898989 100%)',
    text: 'Колір мудрості та рівноваги. Він має гармонію з багатьма кольорами та дарує відчуття захищеності. І саме цей колір ми бачимо миттєво, коли вимикаємо світло в кімнаті.'
  }
];

const CHIPS = {
  green: {
    id: 'green',
    label: 'Зелений',
    background: 'linear-gradient(126deg, #169500 15.893%, #216B14 85.042%)',
    borderColor: '#2bb612',
    color: '#86ff61'
  },
  orange: {
    id: 'orange',
    label: 'Помаранчевий',
    background: 'linear-gradient(126deg, #E68E00 15.893%, #954500 85.042%)',
    borderColor: '#fea63a',
    color: '#ffcf66'
  },
  pink: {
    id: 'pink',
    label: 'Рожевий',
    background: 'linear-gradient(126deg, #CF07A4 15.893%, #9C005B 85.042%)',
    borderColor: '#ff21cf',
    color: '#fda2ff'
  },
  white: {
    id: 'white',
    label: 'Білий',
    background: 'linear-gradient(126deg, #FFFFFF 15.893%, #ADADAD 85.042%)',
    borderColor: '#858585',
    color: '#272727'
  },
  red: {
    id: 'red',
    label: 'Червоний',
    background: 'linear-gradient(126deg, #E60000 15.893%, #8C0000 85.042%)',
    borderColor: '#ed2727',
    color: '#ff8e8e'
  }
};

const CHIP_ORDER = ['white', 'red', 'green', 'pink', 'orange'];

const TARGETS = [
  {
    id: 'trust',
    chipId: 'green',
    text: 'Підвищення довіри, відчуття молодості'
  },
  { id: 'calm', chipId: 'orange', text: 'Впевненість, зниження напруги' },
  { id: 'depth', chipId: 'pink', text: 'Глибина та загадковість' },
  { id: 'tradition', chipId: 'white', text: 'Традиційність, постійність' },
  { id: 'order', chipId: 'red', text: 'Строгість та порядок' }
];

const SWIPE_STEP = 36;

const WHEEL_STEP = 40;

const WHEEL_RESET = 220;

const SUPPORTS_POINTER =
  typeof window !== 'undefined' && 'PointerEvent' in window;

const PROJECT_ID = '23UA-LM-4-test';

const EngagementScreen = ({ handleFinish, isActive = true }) => {
  const { init, sendEvent } = useContext(DataLayerEventContext);

  const [activeColor, setActiveColor] = useState(COLORS[0].id);
  const [isHintVisible, setIsHintVisible] = useState(true);

  const [placed, setPlaced] = useState({});
  const [drag, setDrag] = useState(null);
  const [isReturning, setIsReturning] = useState(false);
  const [isCtaFloating, setIsCtaFloating] = useState(true);

  const startPointRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const placedRef = useRef({});
  const targetRefs = useRef({});
  const returnTimerRef = useRef(null);
  const swipeRef = useRef({ active: false, x: 0, moved: false });
  const wheelRef = useRef(0);
  const wheelLockRef = useRef(false);
  const wheelResetRef = useRef(null);
  const ctaRef = useRef(null);
  const gameRef = useRef(null);

  placedRef.current = placed;

  useEffect(() => {
    return () => {
      clearTimeout(returnTimerRef.current);
      clearTimeout(wheelResetRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;

    init(PROJECT_ID);
    sendEvent('seen_engagement_content');
  }, [isActive]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const blocking = new Set();

    const update = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) blocking.add(entry.target);
        else blocking.delete(entry.target);
      });

      setIsCtaFloating(blocking.size === 0);
    };

    const ctaObserver = new IntersectionObserver(update, { threshold: 0.6 });
    const gameObserver = new IntersectionObserver(update, {
      rootMargin: '-80% 0px 0px 0px'
    });

    if (ctaRef.current) ctaObserver.observe(ctaRef.current);
    if (gameRef.current) gameObserver.observe(gameRef.current);

    return () => {
      ctaObserver.disconnect();
      gameObserver.disconnect();
    };
  }, []);

  const activeColorData =
    COLORS.find((item) => item.id === activeColor) || COLORS[0];

  const handleStripClick = (id) => {
    if (swipeRef.current.moved) return;

    setIsHintVisible(false);
    setActiveColor(id);
  };

  const shiftColor = (direction) => {
    setIsHintVisible(false);
    setActiveColor((prev) => {
      const index = COLORS.findIndex((item) => item.id === prev);
      const next = Math.min(Math.max(index + direction, 0), COLORS.length - 1);

      return COLORS[next].id;
    });
  };

  const startSwipe = (x) => {
    swipeRef.current = { active: true, x, moved: false };
  };

  const continueSwipe = (x) => {
    const swipe = swipeRef.current;
    if (!swipe.active || swipe.moved) return;

    const shift = x - swipe.x;
    if (Math.abs(shift) < SWIPE_STEP) return;

    shiftColor(shift < 0 ? 1 : -1);
    swipe.moved = true;
  };

  const stopSwipe = () => {
    swipeRef.current.active = false;
  };

  const handleColorsWheel = (event) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    clearTimeout(wheelResetRef.current);
    wheelResetRef.current = setTimeout(() => {
      wheelRef.current = 0;
      wheelLockRef.current = false;
    }, WHEEL_RESET);

    if (wheelLockRef.current) return;

    wheelRef.current += event.deltaX;

    if (Math.abs(wheelRef.current) < WHEEL_STEP) return;

    shiftColor(wheelRef.current > 0 ? 1 : -1);
    wheelRef.current = 0;
    wheelLockRef.current = true;
  };

  const colorsSwipeHandlers = SUPPORTS_POINTER
    ? {
        onPointerDown: (event) => startSwipe(event.clientX),
        onPointerMove: (event) => continueSwipe(event.clientX),
        onPointerUp: stopSwipe,
        onPointerCancel: stopSwipe,
        onPointerLeave: stopSwipe,
        onWheel: handleColorsWheel
      }
    : {
        onTouchStart: (event) => {
          const touch = event.touches[0];
          if (touch) startSwipe(touch.clientX);
        },
        onTouchMove: (event) => {
          const touch = event.touches[0];
          if (touch) continueSwipe(touch.clientX);
        },
        onTouchEnd: stopSwipe,
        onTouchCancel: stopSwipe,
        onWheel: handleColorsWheel
      };

  const renderCtaButtons = () => (
    <>
      <button type="button" className={styles.ctaButton} onClick={handleFinish}>
        Залишити відгук
      </button>
      <button
        type="button"
        aria-label="Далі"
        className={styles.ctaArrow}
        onClick={handleFinish}
      >
        <ArrowIcon />
      </button>
    </>
  );

  const isChipPlaced = (chipId) =>
    TARGETS.some((target) => placed[target.id] === chipId);

  const isAllPlaced = TARGETS.every((target) => Boolean(placed[target.id]));

  const findTargetAt = (x, y) =>
    TARGETS.find((target) => {
      if (placedRef.current[target.id]) return false;

      const node = targetRefs.current[target.id];
      if (!node) return false;

      const rect = node.getBoundingClientRect();

      return (
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      );
    }) || null;

  const beginDrag = (chipId, x, y) => {
    if (TARGETS.some((target) => placedRef.current[target.id] === chipId)) {
      return;
    }

    clearTimeout(returnTimerRef.current);
    startPointRef.current = { x, y };
    dragRef.current = { chipId, dx: 0, dy: 0, overTargetId: null };
    setIsReturning(false);
    setDrag(dragRef.current);
  };

  const moveDrag = (x, y) => {
    const current = dragRef.current;
    if (!current) return;

    const target = findTargetAt(x, y);

    const next = {
      chipId: current.chipId,
      dx: x - startPointRef.current.x,
      dy: y - startPointRef.current.y,
      overTargetId: target ? target.id : null
    };

    dragRef.current = next;
    setDrag(next);
  };

  const endDrag = () => {
    const current = dragRef.current;
    if (!current) return;

    dragRef.current = null;

    const target = TARGETS.find((item) => item.id === current.overTargetId);

    if (target && target.chipId === current.chipId) {
      setPlaced((prev) => ({ ...prev, [target.id]: current.chipId }));
      setIsReturning(false);
      setDrag(null);
      return;
    }

    setIsReturning(true);
    setDrag({ ...current, dx: 0, dy: 0, overTargetId: null });
    returnTimerRef.current = setTimeout(() => {
      setDrag(null);
      setIsReturning(false);
    }, 250);
  };

  useEffect(() => {
    if (!drag) return undefined;

    const onPointerMove = (event) => moveDrag(event.clientX, event.clientY);
    const onPointerUp = () => endDrag();
    const onTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch) return;

      if (event.cancelable) event.preventDefault();
      moveDrag(touch.clientX, touch.clientY);
    };
    const onTouchEnd = () => endDrag();

    if (SUPPORTS_POINTER) {
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    } else {
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onTouchEnd);
      window.addEventListener('touchcancel', onTouchEnd);
    }

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [!drag]);

  return (
    <div className={styles.container}>
      <div className={styles.page}>
        <img className={styles.bg} src={IMAGES.background} alt="" />

        <div className={styles.hero}>
          <video
            className={styles.heroMedia}
            src={IMAGES.heroVideo}
            poster={IMAGES.heroPoster}
            autoPlay
            muted
            loop
            playsInline
          />
          <span className={styles.heroFade} />
          <p className={styles.heroText}>
            Навколо безліч речей, які несвідомо впливають на нас.
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.intro}>
            <span className={styles.glassTop} />
            <p className={styles.introTitle}>
              <i>Відтінки </i>
              <b>твого настрою</b>
            </p>
            <div className={styles.introText}>
              <p>
                Кольори здатні впливати на людину швидше, ніж вона це
                усвідомить.
              </p>
              <p>Дізнайся, як вони працюють з нашим мозком.</p>
            </div>
          </div>

          <p className={styles.sectionTitle}>Кольори</p>

          <div className={styles.strips} {...colorsSwipeHandlers}>
            {COLORS.map((color) => (
              <button
                key={color.id}
                type="button"
                aria-label={color.name}
                className={classNames(
                  styles.strip,
                  color.id === activeColor && styles.stripActive
                )}
                style={{ backgroundImage: color.gradient }}
                onClick={() => handleStripClick(color.id)}
              >
                {color.id === activeColor && isHintVisible && (
                  <span className={styles.hand}>
                    <HandIcon />
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className={styles.colorCard} {...colorsSwipeHandlers}>
            <p className={styles.colorName}>{activeColorData.name}</p>
            <p className={styles.colorText}>{activeColorData.text}</p>
          </div>

          <div className={styles.finalBlock}>
            <p className={styles.finalTitle}>фінал</p>
            <div className={styles.finalText}>
              <p>Тепер ти знаєш, як кольори впливають на нас.</p>
              <p>Наповнюй свій простір відтінками та керуй своєю атмосферою.</p>
            </div>
          </div>

          <div className={styles.gameSection}>
            <span className={styles.glassBottom} />
            <div className={styles.gameIntro}>
              <p className={styles.gameTitle}>Кольори та стани</p>
              <p className={styles.gameText}>
                Перевір, наскільки добре ти відчуваєш кольори! Перетягни
                зображення кожного кольору до стану, який, на твою думку,
                найкраще з ним асоціюється.
              </p>
            </div>

            <div
              className={classNames(
                styles.game,
                isAllPlaced && styles.gameCompleted
              )}
              ref={gameRef}
            >
              <div className={styles.gameColumn}>
                {CHIP_ORDER.map((chipId) => {
                  const chip = CHIPS[chipId];
                  const isPlaced = isChipPlaced(chipId);
                  const isDragged = drag && drag.chipId === chipId;

                  return (
                    <div className={styles.slot} key={chipId}>
                      {!isPlaced && (
                        <div
                          className={classNames(
                            styles.chip,
                            isDragged && styles.chipDragged,
                            isDragged && isReturning && styles.chipReturning
                          )}
                          style={{
                            backgroundImage: chip.background,
                            borderColor: chip.borderColor,
                            color: chip.color,
                            transform: isDragged
                              ? `translate(${drag.dx}px, ${drag.dy}px)`
                              : undefined
                          }}
                          onPointerDown={
                            SUPPORTS_POINTER
                              ? (event) =>
                                  beginDrag(
                                    chipId,
                                    event.clientX,
                                    event.clientY
                                  )
                              : undefined
                          }
                          onTouchStart={
                            SUPPORTS_POINTER
                              ? undefined
                              : (event) => {
                                  const touch = event.touches[0];
                                  if (touch) {
                                    beginDrag(
                                      chipId,
                                      touch.clientX,
                                      touch.clientY
                                    );
                                  }
                                }
                          }
                        >
                          {chip.label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className={styles.gameColumn}>
                {TARGETS.map((target, index) => {
                  const placedChip = CHIPS[placed[target.id]];
                  const isOver =
                    Boolean(drag) && drag.overTargetId === target.id;

                  return (
                    <div
                      key={target.id}
                      className={styles.slot}
                      style={
                        isAllPlaced
                          ? { transitionDelay: `${index * 70}ms` }
                          : undefined
                      }
                      ref={(node) => (targetRefs.current[target.id] = node)}
                    >
                      <div
                        className={classNames(
                          styles.target,
                          isOver && styles.targetActive
                        )}
                      >
                        {target.text}
                      </div>

                      {placedChip && (
                        <div
                          className={classNames(styles.chip, styles.chipPlaced)}
                          style={{
                            backgroundImage: placedChip.background,
                            borderColor: placedChip.borderColor,
                            color: placedChip.color
                          }}
                        >
                          {placedChip.label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={styles.cta} ref={ctaRef}>
            {renderCtaButtons()}
          </div>
        </div>
      </div>

      <div
        className={classNames(
          styles.ctaFloating,
          !isCtaFloating && styles.ctaFloatingHidden
        )}
      >
        {renderCtaButtons()}
      </div>
    </div>
  );
};

export default EngagementScreen;
